import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import AddCardRoundedIcon from "@mui/icons-material/AddCardRounded";
import DashboardCustomizeRoundedIcon from "@mui/icons-material/DashboardCustomizeRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import KpiCards from "./components/scadentar-furnizori/KpiCards";
import OverviewTab from "./components/scadentar-furnizori/OverviewTab";
import RecurentTab from "./components/scadentar-furnizori/RecurentTab";
import OneTimeTab from "./components/scadentar-furnizori/OneTimeTab";
import VendorsTab from "./components/scadentar-furnizori/VendorsTab";
import InvoiceFormModal from "./components/scadentar-furnizori/InvoiceFormModal";
import SplitPaymentModal from "./components/scadentar-furnizori/SplitPaymentModal";
import {
  backfillVendorLinks,
  aiAutoCreateInvoiceFromPdf,
  closeInvoice,
  getInvoiceStatusAudit,
  setInvoiceStatusManual,
  validateInvoicePdfAgainstDb,
  convertFx,
  createInvoice,
  createPayment,
  createRecurrence,
  createVendor,
  deleteInvoice,
  deleteVendor,
  downloadInvoicesZip,
  generateRecurrenceNow,
  getInvoices,
  getInvoicesSummary,
  getRecurrences,
  getVendors,
  getSpvDocumentUrl,
  generateInvoiceFromSpvXml,
  runRecurrenceJob,
  predictCurrentMonthRecurrences,
  importInvoicesFromXmlZip,
  updateInvoice,
  uploadDocument,
} from "./services/scadentarFurnizoriApi";
import { getStoredAuthToken } from "./AuthContext";
import "./styles/scadentarFurnizori.css";

const SCADENTAR_FURNIZORI_RETURN_KEY = "scadentarFurnizori.returnState";
const SCADENTAR_FURNIZORI_RETURN_MAX_AGE_MS = 30 * 60 * 1000;

const DEFAULT_SCADENTAR_FURNIZORI_FILTERS = {
  q: "",
  status: "",
  currency: "",
  due_from: "",
  due_to: "",
  date_preset: "",
};

function getInitialMonthValue(restoreState) {
  if (
    restoreState &&
    Object.prototype.hasOwnProperty.call(restoreState, "month")
  ) {
    return restoreState.month || "";
  }
  return "";
}

function normalizeTabIndex(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric >= 0 && numeric <= 3
    ? numeric
    : fallback;
}

function readScadentarFurnizoriReturnState() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SCADENTAR_FURNIZORI_RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const createdAt = Number(parsed?.createdAt || 0);
    if (createdAt && Date.now() - createdAt > SCADENTAR_FURNIZORI_RETURN_MAX_AGE_MS) {
      window.sessionStorage.removeItem(SCADENTAR_FURNIZORI_RETURN_KEY);
      return null;
    }
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
}

function writeScadentarFurnizoriReturnState(state) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      SCADENTAR_FURNIZORI_RETURN_KEY,
      JSON.stringify(state)
    );
  } catch (_) {}
}

function hasInvoiceProof(row) {
  return Boolean(
    row?.doc_factura_url || row?.invoice_doc_url || row?.document_url || row?.doc_plata_url
  );
}

function getDownloadFilename(response, fallback) {
  const disposition = response?.headers?.["content-disposition"] || "";
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
    } catch (_) {}
  }
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] || fallback;
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "scadentar-facturi.zip";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function decodeJwtPayload(token) {
  try {
    if (!token || typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

function resolveManualStatusUser() {
  const direct =
    localStorage.getItem("jiraDisplayName") ||
    localStorage.getItem("jiraEmail") ||
    localStorage.getItem("email") ||
    localStorage.getItem("username") ||
    localStorage.getItem("user") ||
    "";
  if (String(direct).trim()) return String(direct).trim();

  const payload = decodeJwtPayload(getStoredAuthToken());
  const fromJwt =
    payload?.name ||
    payload?.displayName ||
    payload?.preferred_username ||
    payload?.username ||
    payload?.email ||
    payload?.sub ||
    "";

  return String(fromJwt || "").trim() || "manual-ui";
}

function ScadentarFurnizoriPage() {
  const xmlZipInputRef = React.useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tabFromQuery = String(searchParams.get("tab") || "").toLowerCase();
  const initialTab = tabFromQuery === "furnizori" || tabFromQuery === "vendors"
    ? 3
    : tabFromQuery === "recurent"
      ? 1
      : tabFromQuery === "one-time"
        ? 2
        : 0;
  const restoreStateRef = React.useRef(
    location.state?.restoreScadentarFurnizori || readScadentarFurnizoriReturnState()
  );
  const initialRestoreState = restoreStateRef.current;
  const [tab, setTab] = useState(() =>
    normalizeTabIndex(initialRestoreState?.tab, initialTab)
  );
  const [month, setMonth] = useState(() => getInitialMonthValue(initialRestoreState));
  const [monthTouched, setMonthTouched] = useState(() =>
    Boolean(initialRestoreState?.monthTouched)
  );
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_SCADENTAR_FURNIZORI_FILTERS,
    ...(initialRestoreState?.filters || {}),
  }));
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({
    toPay: 0,
    overdue: 0,
    dueSoon: 0,
    paid: 0,
  });
  const [recurrences, setRecurrences] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorSearch, setVendorSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [listRefreshing, setListRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [isImportingXmlZip, setIsImportingXmlZip] = useState(false);
  const [isPredictingCurrentMonth, setIsPredictingCurrentMonth] = useState(false);
  const [isDownloadingInvoicesZip, setIsDownloadingInvoicesZip] = useState(false);
  const [zipDialogOpen, setZipDialogOpen] = useState(false);
  const [zipDragActive, setZipDragActive] = useState(false);
  const [zipUploadProgress, setZipUploadProgress] = useState(0);
  const [zipProcessing, setZipProcessing] = useState(false);
  const [zipResult, setZipResult] = useState("");
  const [zipResultStats, setZipResultStats] = useState(null);
  const [zipSelectedName, setZipSelectedName] = useState("");
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [splitInvoice, setSplitInvoice] = useState(null);
  const [selectedOverviewInvoiceId, setSelectedOverviewInvoiceId] = useState(
    () => initialRestoreState?.selectedOverviewInvoiceId || null
  );
  const loadSeqRef = React.useRef(0);
  const hasLoadedDataRef = React.useRef(false);
  const overviewSearchRef = React.useRef(String(filters?.q || "").trim());

  const patchInvoiceInState = useCallback((invoicePatch) => {
    const patchId = Number(invoicePatch?.id);
    if (!patchId) return;
    setInvoices((prev) =>
      (prev || []).map((row) =>
        Number(row?.id) === patchId ? { ...row, ...invoicePatch } : row
      )
    );
  }, []);

  const patchVendorInvoicesInState = useCallback((vendorId, invoicePatch) => {
    const normalizedVendorId = Number(vendorId || 0);
    if (!normalizedVendorId || !invoicePatch) return;
    setInvoices((prev) =>
      (prev || []).map((row) =>
        Number(row?.vendor_id || 0) === normalizedVendorId ? { ...row, ...invoicePatch } : row
      )
    );
  }, []);

  const handleOverviewFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleMonthChange = useCallback((value) => {
    setMonth(value);
    setMonthTouched(Boolean(value));
  }, []);

  const handleClearMonthFilter = useCallback(() => {
    setMonth("");
    setMonthTouched(false);
    setFilters({ ...DEFAULT_SCADENTAR_FURNIZORI_FILTERS });
  }, []);

  const loadData = useCallback(async (options = {}) => {
    const silent = Boolean(options?.silent);
    const background = Boolean(options?.background);
    const hasLoadedData = hasLoadedDataRef.current;
    const showFullLoader = !silent && !background && !hasLoadedData;
    const showInlineLoader = !silent && (background || hasLoadedData);
    const requestId = loadSeqRef.current + 1;
    loadSeqRef.current = requestId;

    if (showFullLoader) {
      setListRefreshing(false);
      setLoading(true);
    } else if (showInlineLoader) {
      setListRefreshing(true);
    }
    setError("");
    try {
      const tabParam =
        tab === 1 ? "recurrent" : tab === 2 ? "one-time" : "overview";

      const searchQuery = String(filters?.q || "").trim();
      const isOverviewSearch = tab === 0 && Boolean(searchQuery);
      const hasIssueDateRange = Boolean(filters?.due_from || filters?.due_to);
      const queryMonth = hasIssueDateRange
        ? undefined
        : tab === 0
          ? (!isOverviewSearch && monthTouched ? (month || undefined) : undefined)
          : (month || undefined);
      const effectiveFilters = isOverviewSearch
        ? { q: searchQuery }
        : { ...filters, q: searchQuery || undefined };
      const invoiceQuery = {
        month: queryMonth,
        tab: tabParam,
        pageSize: tab === 0 ? 5000 : 1000,
        ...effectiveFilters,
      };
      if (tab === 0) delete invoiceQuery.status;
      const summaryQuery = {
        month: queryMonth,
        ...effectiveFilters,
      };
      if (isOverviewSearch) {
        delete summaryQuery.status;
        delete summaryQuery.currency;
        delete summaryQuery.due_from;
        delete summaryQuery.due_to;
        delete summaryQuery.date_preset;
      }

      const requests = [
        getInvoices(invoiceQuery),
        getInvoicesSummary(summaryQuery),
      ];
      if (tab === 1) requests.push(getRecurrences());
      if (tab === 3) requests.push(getVendors({ q: vendorSearch || undefined }));

      const responses = await Promise.all(requests);
      if (requestId !== loadSeqRef.current) return;
      const invoiceRes = responses[0];
      const summaryRes = responses[1];
      const recurrenceRes = tab === 1 ? responses[2] : null;
      const vendorsRes = tab === 3 ? responses[tab === 1 ? 3 : 2] : null;

      hasLoadedDataRef.current = true;
      setInvoices(invoiceRes?.data?.items || []);
      setSummary(summaryRes?.data || { toPay: 0, overdue: 0, dueSoon: 0, paid: 0 });
      if (recurrenceRes) setRecurrences(recurrenceRes.data || []);
      if (vendorsRes) setVendors(vendorsRes.data || []);
    } catch (err) {
      if (requestId === loadSeqRef.current) {
        setError(err?.response?.data?.error || err.message || "Eroare la incarcare");
      }
    } finally {
      if (requestId !== loadSeqRef.current) return;
      if (showFullLoader) {
        setLoading(false);
      }
      if (showInlineLoader || silent) {
        setListRefreshing(false);
      }
    }
  }, [month, monthTouched, filters, tab, vendorSearch]);

  const openZipImportDialog = useCallback(() => {
    setZipDialogOpen(true);
    setZipDragActive(false);
    setZipUploadProgress(0);
    setZipProcessing(false);
    setZipResult("");
    setZipResultStats(null);
    setZipSelectedName("");
    if (xmlZipInputRef.current) xmlZipInputRef.current.value = "";
  }, []);

  const closeZipImportDialog = useCallback(() => {
    if (isImportingXmlZip) return;
    setZipDialogOpen(false);
    setZipDragActive(false);
  }, [isImportingXmlZip]);

  const handleXmlZipImport = useCallback(async (file) => {
    if (!file) return;
    const fileName = String(file.name || "").toLowerCase();
    if (!fileName.endsWith(".zip")) {
      setZipResult("Fisier invalid. Te rog incarca un ZIP ANAF.");
      return;
    }

    setIsImportingXmlZip(true);
    setZipSelectedName(file.name || "");
    setZipUploadProgress(0);
    setZipProcessing(false);
    setZipResult("");
    setZipResultStats(null);
    setError("");

    try {
      const response = await importInvoicesFromXmlZip(file, {
        onUploadProgress: (evt) => {
          if (!evt?.total) return;
          const progress = Math.max(1, Math.min(100, Math.round((evt.loaded * 100) / evt.total)));
          setZipUploadProgress(progress);
          if (progress >= 100) setZipProcessing(true);
        },
      });

      const data = response?.data || {};
      const stats = {
        inserted: Number(data?.inserted_count || 0),
        updated: Number(data?.updated_count || 0),
        skipped: Number(data?.skipped_count || 0),
        withLines: Number(data?.with_lines_count || 0),
      };
      const message = data?.message || "Import finalizat cu succes.";

      setZipUploadProgress(100);
      setZipProcessing(false);
      setZipResult(message);
      setZipResultStats(stats);
      setTab(0);
      await loadData();
    } catch (err) {
      setZipProcessing(false);
      const backendMessage =
        err?.response?.data?.error || err?.response?.data?.message || err.message || "Import ZIP XML failed";
      setZipResult("Eroare: " + backendMessage);
      setZipResultStats(null);
    } finally {
      setIsImportingXmlZip(false);
      if (xmlZipInputRef.current) xmlZipInputRef.current.value = "";
    }
  }, [loadData]);

  const handleZipDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setZipDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) handleXmlZipImport(file);
  }, [handleXmlZipImport]);

  const handleZipBrowseChange = useCallback((event) => {
    const file = event.target?.files?.[0];
    if (file) handleXmlZipImport(file);
  }, [handleXmlZipImport]);
  useEffect(() => {
    const currentOverviewSearch = tab === 0 ? String(filters?.q || "").trim() : "";
    const searchFilterChanged = tab === 0 && overviewSearchRef.current !== currentOverviewSearch;
    overviewSearchRef.current = currentOverviewSearch;
    loadData({ background: searchFilterChanged || Boolean(currentOverviewSearch) });
  }, [filters?.q, loadData, tab]);

  useEffect(() => {
    if (restoreStateRef.current) {
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem(SCADENTAR_FURNIZORI_RETURN_KEY);
      }
      restoreStateRef.current = null;
      return;
    }
    setTab(initialTab);
  }, [initialTab]);

  const loadVendorOptions = useCallback(async () => {
    try {
      const response = await getVendors({});
      setVendorOptions(response.data || []);
    } catch (_) {
      setVendorOptions([]);
    }
  }, []);

  useEffect(() => {
    loadVendorOptions();
  }, [loadVendorOptions]);

  useEffect(() => {
    if (showInvoiceModal) {
      loadVendorOptions();
    }
  }, [showInvoiceModal, loadVendorOptions]);

  const openVendorProfile = useCallback(async (row) => {
    let vendorId = row?.vendor_id || (tab === 3 ? row?.id : null) || null;
    const sourceInvoiceId =
      row?.sourceInvoiceId ||
      row?.invoice_id ||
      row?.invoiceId ||
      (tab === 0 && row?.vendor_id ? row?.id : null) ||
      null;
    if (!vendorId && row?.denumire) {
      const created = await createVendor({ denumire: row.denumire });
      vendorId = created?.data?.id || null;
      await loadData();
    }
    if (!vendorId) return;
    const returnState = {
      tab,
      month,
      monthTouched,
      filters,
      selectedOverviewInvoiceId:
        tab === 0 ? sourceInvoiceId || selectedOverviewInvoiceId || null : null,
      createdAt: Date.now(),
    };
    writeScadentarFurnizoriReturnState(returnState);
    navigate(`/scadentar-furnizori/furnizori/${vendorId}`, {
      state: { fromScadentarFurnizori: returnState },
    });
  }, [filters, loadData, month, monthTouched, navigate, selectedOverviewInvoiceId, tab]);

  const runCurrentMonthPrediction = useCallback(async () => {
    setIsPredictingCurrentMonth(true);
    setError("");
    try {
      const response = await predictCurrentMonthRecurrences();
      const created = Number(response?.data?.created || 0);
      const skipped = Number(response?.data?.skipped || 0);
      const failed = Number(response?.data?.failed || 0);
      setInfo(
        `Predictie recurenta rulata pentru luna curenta. Facturi create: ${created}. Existente/sarite: ${skipped}.`
      );
      if (failed > 0) {
        setError(`Predictia a avut ${failed} erori. Poti rula din nou dupa verificare; facturile deja create nu se dubleaza.`);
      }
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Eroare la rularea predictiei");
    } finally {
      setIsPredictingCurrentMonth(false);
    }
  }, [loadData]);

  const handleDownloadInvoicesZip = useCallback(async (options = {}) => {
    const dueFrom = String(filters.due_from || "").trim();
    const dueTo = String(filters.due_to || "").trim();
    const params = {};
    if (dueFrom) params.due_from = dueFrom;
    if (dueTo) params.due_to = dueTo;
    if (!dueFrom && !dueTo && monthTouched && month) params.month = month;
    if (options?.excludeSpv) params.exclude_spv = "1";

    setIsDownloadingInvoicesZip(true);
    setError("");
    setInfo("");
    try {
      const response = await downloadInvoicesZip(params);
      const blob = new Blob([response.data], { type: "application/zip" });
      const filename = getDownloadFilename(response, "scadentar-facturi.zip");
      triggerBlobDownload(blob, filename);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Nu am putut descarca ZIP-ul cu facturi.");
    } finally {
      setIsDownloadingInvoicesZip(false);
    }
  }, [filters.due_from, filters.due_to, month, monthTouched]);

  return (
    <Box className="sf-page">
      <Paper className="sf-header-card">
        <Box className="sf-header-top">
          <Box>
            <Typography className="sf-title">
              <AutoAwesomeRoundedIcon />
              Scadentar Furnizori
            </Typography>
            <Typography className="sf-subtitle">
              Facturi primite de la furnizori (de platit), recurente, one-time, split payments.
            </Typography>
          </Box>
        </Box>
        <KpiCards summary={summary} />
        <Box className="sf-header-actions">
          <Button
            variant="contained"
            className="sf-btn-primary sf-main-action"
            startIcon={<AddCardRoundedIcon />}
            onClick={() => setShowInvoiceModal(true)}
          >
            Adauga factura
          </Button>
          <Button
            variant="outlined"
            className="sf-btn-ghost sf-secondary-action"
            startIcon={isPredictingCurrentMonth ? <CircularProgress size={14} /> : <AutorenewRoundedIcon />}
            disabled={isPredictingCurrentMonth}
            onClick={runCurrentMonthPrediction}
          >
            Predictii luna curenta
          </Button>
          <Button
            variant="outlined"
            className="sf-btn-ghost sf-secondary-action"
            startIcon={isImportingXmlZip ? <CircularProgress size={14} /> : <UploadFileRoundedIcon />}
            disabled={isImportingXmlZip}
            onClick={openZipImportDialog}
          >
            Import SPV
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}
      {info && <Alert severity="info">{info}</Alert>}

      <Paper className="sf-tabs-shell">
        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab icon={<DashboardCustomizeRoundedIcon />} iconPosition="start" label="Overview" />
          <Tab icon={<AutorenewRoundedIcon />} iconPosition="start" label="Recurent" />
          <Tab icon={<ReceiptLongRoundedIcon />} iconPosition="start" label="One-time" />
          <Tab icon={<ApartmentRoundedIcon />} iconPosition="start" label="Furnizori" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box className="sf-loading-wrap">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tab === 0 && (
            <OverviewTab
              invoices={invoices}
              selectedInvoiceId={selectedOverviewInvoiceId}
              onSelectInvoice={setSelectedOverviewInvoiceId}
              month={month}
              onMonthChange={handleMonthChange}
              onClearMonth={handleClearMonthFilter}
              filters={filters}
              onFilterChange={handleOverviewFilterChange}
              isRefreshing={listRefreshing}
              onSplit={(row) => setSplitInvoice(row)}
              onClose={async (row) => {
                if (!hasInvoiceProof(row)) {
                  setError("Nu poti inchide factura fara dovada (document/PDF/imagine).");
                  return;
                }
                await closeInvoice(row.id, {});
                await loadData();
              }}
              onRefresh={loadData}
              onRefreshQuiet={() => loadData({ silent: true })}
              onDownloadInvoicesZip={handleDownloadInvoicesZip}
              isDownloadingInvoicesZip={isDownloadingInvoicesZip}
              onOpenVendor={openVendorProfile}
              onOpenSpvDocument={(row) => {
                if (!row?.external_id) return;
                const url = getSpvDocumentUrl(row.external_id);
                window.open(url, "_blank", "noopener,noreferrer");
              }}
              onQuickPayment={async (row, payload) => {
                const response = await createPayment(row.id, payload);
                return response?.data || null;
              }}
              onUpload={async (file, meta = {}) => {
                const response = await uploadDocument(file, meta);
                return response?.data || null;
              }}
              onValidateInvoicePdf={async (row, file) => {
                const response = await validateInvoicePdfAgainstDb(row?.id, file);
                return response?.data || null;
              }}
              onGenerateFromSpv={async (row) => {
                console.info("[SF][frontend][onGenerateFromSpv] start", {
                  invoice_id: row?.id || null,
                  external_id: row?.external_id || null,
                  source: row?.source || null,
                });
                const response = await generateInvoiceFromSpvXml(row?.external_id, {
                  invoice_id: row?.id,
                });
                console.info("[SF][frontend][onGenerateFromSpv] done", {
                  status: response?.status,
                  action: response?.data?.action || null,
                  invoice_id: response?.data?.invoice?.id || null,
                });
                return response?.data || null;
              }}
              onSaveInvoice={async (row, payload) => {
                const response = await updateInvoice(row.id, payload);
                return response?.data || null;
              }}
              onSetManualStatus={async (row, payload) => {
                const changedBy = resolveManualStatusUser();
                const response = await setInvoiceStatusManual(row.id, {
                  ...payload,
                  changed_by: changedBy,
                  override_business_rules: true,
                });
                return response?.data || null;
              }}
              onGetStatusAudit={async (row, limit = 50) => {
                const response = await getInvoiceStatusAudit(row.id, { limit });
                return Array.isArray(response?.data) ? response.data : [];
              }}
              onDeleteInvoice={async (row) => {
                await deleteInvoice(row.id);
                setSelectedOverviewInvoiceId(null);
                await loadData({ silent: true });
              }}
              onDeleteInvoicesBulk={async (rows) => {
                const targets = (rows || []).filter((row) => row?.id);
                const results = await Promise.allSettled(
                  targets.map((row) => deleteInvoice(row.id))
                );
                setSelectedOverviewInvoiceId(null);
                await loadData({ silent: true });
                const failed = results.filter((result) => result.status === "rejected");
                if (failed.length) {
                  throw new Error(
                    `Nu am putut sterge ${failed.length} din ${targets.length} facturi selectate.`
                  );
                }
                return { deleted: targets.length };
              }}
              onPatchInvoice={patchInvoiceInState}
              onPatchVendorInvoices={patchVendorInvoicesInState}
              onPreviewFx={async ({ valuta, suma_valuta, data_emitere }) => {
                const response = await convertFx({
                  valuta,
                  suma_valuta: Number(suma_valuta || 0),
                  data_emitere,
                });
                return {
                  sumaRon: Number(response.data?.sumaRon || 0),
                  cursBnr: response.data?.cursBnr || null,
                  cursDate: response.data?.cursDate || null,
                };
              }}
            />
          )}
          {tab === 1 && (
            <RecurentTab
              recurrences={recurrences}
              invoices={invoices}
              onCreateRecurrence={async (payload) => {
                await createRecurrence(payload);
                await loadData();
              }}
              onGenerateNow={async (id) => {
                await generateRecurrenceNow(id);
                await loadData();
              }}
              onRunJob={async () => {
                await runRecurrenceJob();
                await loadData();
              }}
              onPredictCurrentMonth={runCurrentMonthPrediction}
              onOpenVendor={openVendorProfile}
              onOpenSpvDocument={(row) => {
                if (!row?.external_id) return;
                const url = getSpvDocumentUrl(row.external_id);
                window.open(url, "_blank", "noopener,noreferrer");
              }}
            />
          )}
          {tab === 2 && (
            <OneTimeTab
              invoices={invoices}
              filters={filters}
              onFilterChange={(key, value) => setFilters((s) => ({ ...s, [key]: value }))}
              onSplit={(row) => setSplitInvoice(row)}
              onClose={async (row) => {
                if (!hasInvoiceProof(row)) {
                  setError("Nu poti inchide factura fara dovada (document/PDF/imagine).");
                  return;
                }
                await closeInvoice(row.id, {});
                await loadData();
              }}
              onRefresh={loadData}
              onOpenVendor={openVendorProfile}
            />
          )}
          {tab === 3 && (
            <VendorsTab
              vendors={vendors}
              searchTerm={vendorSearch}
              onSearchChange={setVendorSearch}
              onCreateVendor={async (payload) => {
                await createVendor(payload);
                await loadData();
              }}
              onOpenVendor={openVendorProfile}
              onDeleteVendor={async (vendor) => {
                await deleteVendor(vendor.id);
                await loadData({ silent: true });
              }}
              onRefresh={loadData}
              onBackfill={async () => {
                await backfillVendorLinks();
                await loadData();
              }}
            />
          )}
        </>
      )}

      <Dialog open={zipDialogOpen} onClose={closeZipImportDialog} fullWidth maxWidth="sm">
        <DialogTitle>Import facturi SPV din ZIP</DialogTitle>
        <DialogContent>
          <input
            ref={xmlZipInputRef}
            type="file"
            accept=".zip,application/zip"
            style={{ display: "none" }}
            onChange={handleZipBrowseChange}
          />
          <Box
            className={"sf-doc-dropzone " + (zipDragActive ? "is-active" : "")}
            sx={{ mt: 1 }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setZipDragActive(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setZipDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setZipDragActive(false);
            }}
            onDrop={handleZipDrop}
            onClick={() => {
              if (!isImportingXmlZip) xmlZipInputRef.current?.click();
            }}
          >
            <UploadFileRoundedIcon className="sf-doc-dropzone-icon" />
            <Typography className="sf-doc-dropzone-title">Drag & drop ZIP ANAF</Typography>
            <Typography className="sf-doc-dropzone-sub">sau click pentru browse</Typography>
            {zipSelectedName ? (
              <Typography sx={{ mt: 1, fontSize: 12, color: "#475569" }}>
                Fisier selectat: {zipSelectedName}
              </Typography>
            ) : null}
          </Box>

          {(isImportingXmlZip || zipUploadProgress > 0) && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 12, color: "#475569", mb: 0.75 }}>
                {zipProcessing ? "Upload complet. Procesare XML in backend..." : "Upload ZIP: " + zipUploadProgress + "%"}
              </Typography>
              {zipProcessing ? (
                <LinearProgress />
              ) : (
                <LinearProgress variant="determinate" value={zipUploadProgress} />
              )}
            </Box>
          )}

          {zipResult ? (
            <Alert sx={{ mt: 2 }} severity={zipResult.startsWith("Eroare:") ? "error" : "success"}>
              {zipResult}
            </Alert>
          ) : null}

          {zipResultStats ? (
            <Box
              sx={{
                mt: 1.5,
                border: "1px solid #dbeafe",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  bgcolor: "#f8fbff",
                  borderBottom: "1px solid #dbeafe",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#1e3a8a",
                }}
              >
                Rezultat import
              </Box>
              <Stack spacing={0} divider={<Divider flexItem />}>
                <Box sx={{ px: 1.5, py: 0.9, display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 13, color: "#334155" }}>Inserate</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>
                    {zipResultStats.inserted}
                  </Typography>
                </Box>
                <Box sx={{ px: 1.5, py: 0.9, display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 13, color: "#334155" }}>Actualizate</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0369a1" }}>
                    {zipResultStats.updated}
                  </Typography>
                </Box>
                <Box sx={{ px: 1.5, py: 0.9, display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 13, color: "#334155" }}>Skip</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
                    {zipResultStats.skipped}
                  </Typography>
                </Box>
                <Box sx={{ px: 1.5, py: 0.9, display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 13, color: "#334155" }}>Cu linii factura</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1d4ed8" }}>
                    {zipResultStats.withLines}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeZipImportDialog} disabled={isImportingXmlZip}>Inchide</Button>
          <Button
            variant="contained"
            onClick={() => xmlZipInputRef.current?.click()}
            disabled={isImportingXmlZip}
            startIcon={<UploadFileRoundedIcon />}
          >
            Browse ZIP
          </Button>
        </DialogActions>
      </Dialog>
      <InvoiceFormModal
        open={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        vendors={vendorOptions}
        onSubmit={async (payload) => {
          await createInvoice(payload);
          await loadData();
        }}
        onUpload={async (file, meta = {}) => {
          const response = await uploadDocument(file, meta);
          return response.data;
        }}
        onPreviewFx={async ({ valuta, suma_valuta, data_emitere }) => {
          const response = await convertFx({
            valuta,
            suma_valuta: Number(suma_valuta || 0),
            data_emitere,
          });
          return {
            sumaRon: Number(response.data?.sumaRon || 0),
            cursBnr: response.data?.cursBnr || null,
            cursDate: response.data?.cursDate || null,
          };
        }}
        onAiAutoImport={async ({ file, deferReload = false }) => {
          const response = await aiAutoCreateInvoiceFromPdf({
            file,
            created_by: "AI_AUTO",
          });
          const payload = response?.data || null;
          const invoice = payload?.invoice || null;
          const action = String(payload?.action || "inserted").toLowerCase();

          if (!deferReload) {
            setTab(0);
            setFilters({
              q: "",
              status: "",
              currency: "",
              due_from: "",
              due_to: "",
              date_preset: "",
            });
            await loadData();
          }

          const label =
            action === "updated"
              ? "actualizata"
              : action === "skipped"
                ? "identica - skip"
                : "importata";

          setInfo(
            `AI Auto: factura ${invoice?.numar || invoice?.factura || invoice?.id || "-"} ${label}.`
          );

          return payload;
        }}
      />

      <SplitPaymentModal
        open={Boolean(splitInvoice)}
        invoice={splitInvoice}
        onClose={() => setSplitInvoice(null)}
        onUpload={async (file, meta = {}) => {
          const response = await uploadDocument(file, meta);
          return response.data;
        }}
        onSubmit={async (payload) => {
          if (!splitInvoice) return;
          await createPayment(splitInvoice.id, payload);
          await loadData();
        }}
      />
    </Box>
  );
}

export default ScadentarFurnizoriPage;
